/**
 * mastering-reference.js — reference-track matching (window.MasteringReference)
 *
 * The paid feature every competitor gates (LANDR/eMastered/RoEx/Kits) — here it's
 * free. Given a TARGET and a REFERENCE track, it derives how to make the target's
 * tonal balance and stereo width resemble the reference (the Matchering idea:
 * match RMS, frequency response, and stereo width).
 *
 * Design choice: instead of a Matchering-style linear-phase FIR (FFT convolution
 * — ringing, latency, and an extra full-length buffer, which the low-memory
 * class this tool already fights cannot afford), we emit a small set of biquads
 * — the ones the mastering chain already runs. What matters is not the number of
 * filters but that the curve they ACTUALLY produce is the curve we computed.
 *
 * ── How this is fitted, and why (measured 2026-07-29, ground-truth bench) ────
 * The first version asked for a gain per band and set each peaking filter to it.
 * Filters an octave apart with Q=1 overlap, so their dB responses ADD: asking
 * for +6 dB in every band delivered up to +10.8, the promised ±6 dB clamp was
 * really ±8.6, and around 800 Hz the applied curve could come out with the
 * OPPOSITE sign to the one computed.
 *
 * So the gains are now solved, not assigned: build the desired curve on a fine
 * log grid, then least-squares-fit the filter set to it and correct twice
 * against the true cascade response. The bench (a real track pushed through a
 * KNOWN biquad chain — the correction is then analytically known) scored, over
 * 5 sources × 13 curves:
 *
 *   shipped 9-band direct :  0.78 dB mean error, 4.07 worst, up to 8.6 dB applied
 *   this design           :  0.18 dB mean error, 1.53 worst, capped at 8.0 dB
 *   ceiling of this filter set (fed the exact answer): 0.16 dB — i.e. the fit is
 *   at 88% of what these filters can express; more bands buy nothing real.
 *
 * Each ingredient was measured separately, and the losers are recorded so nobody
 * re-adds them: a rectangular analysis window costs 2.9× accuracy (that one was
 * a plain bug — on tonal material its leakage misreads a band by tens of dB);
 * ±1/2-octave integration 2.4×; FFT 4096 2.1×; dropping the edge shelves 2.6×.
 * Tried and rejected because they made it WORSE: 1/2, 1/3 and 1/4-octave grids
 * (3× the filters, no gain outside narrow notches), per-bin confidence weighting
 * (it mutes the honestly-quiet top end of real music), de-meaning the desired
 * curve, and capping the air shelf.
 *
 * API:
 *   MasteringReference.analyze(targetBuffer, referenceBuffer) ->
 *     { bands: [{type, freq, q, gainDb}], widthFactor, ok }
 */
(function () {
  'use strict';

  // Iterative radix-2 FFT (forward only needed). re/im are Float64Array, len 2^k.
  function fft(re, im) {
    var n = re.length;
    for (var i = 1, j = 0; i < n; i++) {
      var bit = n >> 1;
      for (; j & bit; bit >>= 1) j ^= bit;
      j ^= bit;
      if (i < j) { var tr = re[i]; re[i] = re[j]; re[j] = tr; var ti = im[i]; im[i] = im[j]; im[j] = ti; }
    }
    for (var len = 2; len <= n; len <<= 1) {
      var ang = -2 * Math.PI / len, wr = Math.cos(ang), wi = Math.sin(ang);
      for (var s = 0; s < n; s += len) {
        var cr = 1, ci = 0;
        for (var k = 0; k < (len >> 1); k++) {
          var i1 = s + k, i2 = s + k + (len >> 1);
          var xr = re[i2] * cr - im[i2] * ci, xi = re[i2] * ci + im[i2] * cr;
          re[i2] = re[i1] - xr; im[i2] = im[i1] - xi;
          re[i1] += xr; im[i1] += xi;
          var ncr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = ncr;
        }
      }
    }
  }

  // Nine perceptual centres. Kept as the module's public band axis: the genre
  // table below is expressed on it, and /mix-analyzer reads bandLevels() on it.
  var CENTERS = [60, 120, 250, 500, 1000, 2000, 4000, 8000, 12000];

  /* ── Built-in genre reference profiles ────────────────────────────────────
     Asked for by a paying user 2026-07-27: «а у вас есть эталонный трек для
     мастеринга, не могли бы вы сделать?» — he was uploading his own reference
     on ~95% of his masters and wanted a professional one from us.

     We cannot hand out commercial recordings (that is distributing someone
     else's master), and we do not need to: the matcher never uses the reference
     audio itself, only its average tonal shape and stereo width. So a profile
     IS the reference, minus the copyright problem.

     BASELINE is the average band shape of real mastered music, MEASURED here
     (2026-07-26) across four mastered fixtures with this very analyzer, in dB
     relative to each track's own mean. It matters that this is measured and not
     invented: music falls off ~26 dB from its low-mid peak to 12 kHz, so a
     "flat" target would tell the matcher to boost the top end by the full clamp
     on every track and every genre would come out identical — which is exactly
     what the first version of this table did before the test caught it.

     The 2026-07-29 window fix re-measures every band slightly: checked against
     the same class of material, the shift on real music is ≤1 dB per band, so
     the table stands as measured. (On a pure tone the two analyzers disagree by
     30+ dB — that is the leakage bug being fixed, not the table drifting.)

     Each genre is BASELINE + a small delta reflecting how that genre is
     conventionally mastered. `width` is a gentle RELATIVE multiplier on stereo
     width (not an absolute target — an absolute one pins to the clamp on any
     track whose width differs from the assumed one). */
  var BASELINE = [-3.5, 7.7, 7.0, 4.3, -2.5, -9.4, -12.8, -18.6, -26.6];
  var GENRE_DELTAS = {
    hiphop:    { d: [ 3.0,  1.5, -1.0, -1.5, -0.5,  0.0,  0.5,  1.0,  0.0], width: 0.98 },
    pop:       { d: [ 0.0,  0.0, -0.5, -0.5,  0.0,  0.5,  1.5,  2.0,  1.5], width: 1.05 },
    rock:      { d: [-1.0,  0.0,  0.5,  1.0,  1.0,  1.0,  0.5,  0.0, -0.5], width: 1.00 },
    edm:       { d: [ 3.0,  1.0, -1.0, -1.5, -0.5,  0.0,  1.0,  2.0,  2.0], width: 1.12 },
    acoustic:  { d: [-1.0, -0.5,  0.0,  0.5,  0.5,  0.5,  0.5,  1.0,  1.0], width: 1.05 },
    classical: { d: [-1.5, -0.5,  0.5,  1.0,  1.0,  0.5,  0.0,  0.0,  0.0], width: 1.08 },
    lofi:      { d: [ 1.0,  2.0,  1.0,  0.5,  0.0, -1.0, -2.0, -3.5, -5.0], width: 0.92 },
    // Requested by the same user 2026-07-28: «добавить жанры джаз, транс и регги».
    // Each delta encodes what actually defines the genre on a master bus:
    // jazz — acoustic and dynamic, near-neutral with a little upright-bass body
    //        and air, wide because the room IS the record, never bass-boosted;
    // trance — the EDM family taken further: deep sub, scooped low-mids to keep
    //        the kick clear, lifted air, and the widest stereo of any preset;
    // reggae — defined by the bass. Heaviest low end here, mids pulled back for
    //        the skank, top deliberately soft/dark, and kept narrow because the
    //        weight has to survive a mono sound system.
    jazz:      { d: [-1.0,  0.5,  1.0,  1.0,  0.5,  0.0,  0.0,  0.5,  0.5], width: 1.10 },
    trance:    { d: [ 3.0,  1.0, -1.5, -2.0, -1.0,  0.0,  1.0,  2.5,  3.0], width: 1.18 },
    reggae:    { d: [ 4.0,  2.5, -0.5, -1.0, -1.0, -1.0, -1.5, -2.0, -2.5], width: 0.95 }
  };
  var GENRE_PROFILES = (function () {
    var out = {}, k, i;
    for (k in GENRE_DELTAS) {
      if (!GENRE_DELTAS.hasOwnProperty(k)) continue;
      var db = [];
      for (i = 0; i < BASELINE.length; i++) db.push(BASELINE[i] + GENRE_DELTAS[k].d[i]);
      out[k] = { db: db, width: GENRE_DELTAS[k].width };
    }
    return out;
  })();

  /* ── Tuning constants (every one of them measured, see the header) ───────── */
  var FFT_SIZE      = 8192;   // 4096 costs 2.1× accuracy; 16384 buys nothing
  var LOUD_FRAC     = 0.5;    // analyse the loudest half of the track
  var GRID_POINTS   = 256;    // resolution of the desired curve
  var INTEG_OCT     = 1 / 6;  // ±1/6 octave around each grid point
  var BAND_INTEG    = 0.5;    // ±1/2 octave for the 9-band public axis
  var GAIN_CAP_DB   = 12;     // no single filter may exceed this
  var CURVE_CAP_DB  = 8;      // and the curve they produce together stays under
                              // this — the shipped code promised 6 and delivered
                              // 8.6, so 8 keeps the tool no more aggressive than
                              // it already was while making the shape correct
  var PROFILE_CAP_DB = 6;     // genre presets keep their original ±6 dB intent
  /* Авторежим правит ШИРЕ жанрового пресета — и это не произвол, а разные
     задачи. Жанр — мягкое пожелание («сделай похоже на хип-хоп»), и ±6 дБ там
     ровно та осторожность, что нужна. Авто зовут, когда с записью что-то не
     так: у винил-рипа 1972 года шипение может стоять на 10+ дБ выше нормы, и
     потолок 6 дБ означает, что за один прогон исправить нельзя — замерено,
     второй прогон продолжал двигать спектр ещё на 2 дБ. 9 дБ хватает, чтобы
     сойтись за раз, и всё ещё далеко от «слепого» ±12 дБ одного фильтра. */
  var AUTO_CAP_DB    = 9;
  var SIDE_CAP_DB   = 8;      // the width curve gets the same ceiling as the tone one
  var SIDE_MONO_HZ  = 120;    // below this the width is left alone (mono/vinyl safety)
  var SIDE_MIN_RATIO = 0.02;  // a track this close to mono has no width to correct —
                              // "widening" it would only amplify room noise
  var RIDGE         = 0.05;   // keeps the normal equations well conditioned

  /* ── analysis ─────────────────────────────────────────────────────────────*/

  var hannCache = {};
  function hann(N) {
    if (hannCache[N]) return hannCache[N];
    var w = new Float64Array(N);
    for (var i = 0; i < N; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / N);
    hannCache[N] = w;
    return w;
  }

  // Largest usable frame size for this material: a short clip must still get
  // several frames to average over.
  function fftSizeFor(len) {
    var N = FFT_SIZE;
    while (N > 1024 && len < 4 * N) N >>= 1;
    return N;
  }

  // Average power spectrum over the LOUDEST frames, Hann-windowed.
  // A rectangular window (what this used to do) leaks: on tonal material it
  // reports energy in bands that hold none.
  function powerSpectrum(chan, N) {
    var half = N >> 1;
    var nFrames = Math.floor(chan.length / N);
    if (nFrames < 1) nFrames = 1;
    var energies = [], f, i;
    for (f = 0; f < nFrames; f++) {
      var base = f * N, e = 0;
      for (i = 0; i < N; i++) { var s = chan[base + i] || 0; e += s * s; }
      energies.push({ f: f, e: e });
    }
    energies.sort(function (a, b) { return b.e - a.e; });
    var keep = Math.max(1, Math.ceil(energies.length * LOUD_FRAC));
    var acc = new Float64Array(half);
    var re = new Float64Array(N), im = new Float64Array(N), w = hann(N);
    for (var kf = 0; kf < keep; kf++) {
      var b0 = energies[kf].f * N;
      for (var t = 0; t < N; t++) { re[t] = (chan[b0 + t] || 0) * w[t]; im[t] = 0; }
      fft(re, im);
      for (var kk = 0; kk < half; kk++) acc[kk] += re[kk] * re[kk] + im[kk] * im[kk];
    }
    return acc;
  }

  // RMS level at fc, integrating ±octFrac octaves of the power spectrum.
  function levelAt(acc, N, sr, fc, octFrac) {
    var half = acc.length;
    var klo = Math.max(1, Math.round(fc * Math.pow(2, -octFrac) * N / sr));
    var khi = Math.min(half - 1, Math.round(fc * Math.pow(2, octFrac) * N / sr));
    if (khi < klo) khi = klo;
    var s = 0;
    for (var k = klo; k <= khi; k++) s += acc[k];
    return Math.sqrt(s / (khi - klo + 1));
  }

  // Public 9-band vector (used by /mix-analyzer and by the genre path).
  function bandLevels(chan, sr) {
    var N = fftSizeFor(chan.length);
    var acc = powerSpectrum(chan, N);
    var out = [];
    for (var c = 0; c < CENTERS.length; c++) out.push(levelAt(acc, N, sr, CENTERS[c], BAND_INTEG));
    return out;
  }

  function mid(buffer) {
    var n = buffer.length;
    if (buffer.numberOfChannels < 2) return buffer.getChannelData(0);
    var L = buffer.getChannelData(0), R = buffer.getChannelData(1), m = new Float32Array(n);
    for (var i = 0; i < n; i++) m[i] = (L[i] + R[i]) * 0.5;
    return m;
  }
  function side(buffer) {
    var n = buffer.length;
    if (buffer.numberOfChannels < 2) return new Float32Array(0);
    var L = buffer.getChannelData(0), R = buffer.getChannelData(1), s = new Float32Array(n);
    for (var i = 0; i < n; i++) s[i] = (L[i] - R[i]) * 0.5;
    return s;
  }
  function sideRms(buffer) {
    if (buffer.numberOfChannels < 2) return 0;
    var L = buffer.getChannelData(0), R = buffer.getChannelData(1), n = buffer.length, s = 0;
    for (var i = 0; i < n; i++) { var v = (L[i] - R[i]) * 0.5; s += v * v; }
    return Math.sqrt(s / n);
  }
  function midRms(buffer) {
    return rmsOf(mid(buffer)) || 1e-9;
  }
  function rmsOf(chan) {
    var n = chan.length, s = 0;
    for (var i = 0; i < n; i++) s += chan[i] * chan[i];
    return n ? Math.sqrt(s / n) : 0;
  }

  /* ── the filter set and its true response ─────────────────────────────────*/

  // ISO octave centres plus a shelf at each edge, so the sub and the air — which
  // the old 60 Hz…12 kHz peaking-only set could not reach — are covered too.
  function filterSet(sr) {
    var nyqSafe = 0.45 * sr, out = [], f;
    for (f = 31.5; f <= 16000 * 1.0001; f *= 2) {
      if (f <= nyqSafe) out.push({ type: 'peaking', freq: f, q: 1.0 });
    }
    if (!out.length) return out;
    out.unshift({ type: 'lowshelf', freq: Math.max(18, out[0].freq / Math.SQRT2), q: 0.707 });
    out.push({ type: 'highshelf', freq: Math.min(nyqSafe, out[out.length - 1].freq * Math.SQRT2), q: 0.707 });
    return out;
  }

  // One FIXED grid in Hz, independent of sample rate: that is what makes a
  // reference reducible to a portable profile (see profileOf) — a 48 kHz
  // reference and a 44.1 kHz track are then comparable point by point. Points
  // above a given track's Nyquist are simply not fitted (see prepare).
  var GRID = (function () {
    var lo = 20, hi = 19500, g = [];
    for (var i = 0; i < GRID_POINTS; i++) g.push(lo * Math.pow(hi / lo, i / (GRID_POINTS - 1)));
    return g;
  })();
  function makeGrid() { return GRID; }

  // Trig terms of e^{-jw} and e^{-2jw} at every grid point, shared by all filters.
  function gridTrig(grid, sr) {
    var t = [];
    for (var i = 0; i < grid.length; i++) {
      var w = 2 * Math.PI * grid[i] / sr;
      t.push({ c1: Math.cos(w), s1: Math.sin(w), c2: Math.cos(2 * w), s2: Math.sin(2 * w) });
    }
    return t;
  }

  // RBJ coefficients — identical to what BiquadFilterNode will run.
  function coeffs(type, f0, gainDb, q, sr) {
    var w0 = 2 * Math.PI * f0 / sr, cw = Math.cos(w0), sw = Math.sin(w0);
    var A = Math.pow(10, gainDb / 40);
    if (type === 'peaking') {
      var al = sw / (2 * q);
      return { b0: 1 + al * A, b1: -2 * cw, b2: 1 - al * A, a0: 1 + al / A, a1: -2 * cw, a2: 1 - al / A };
    }
    var alS = sw / 2 * Math.SQRT2, sA = 2 * Math.sqrt(A) * alS;   // shelf slope S = 1
    if (type === 'lowshelf') {
      return {
        b0: A * ((A + 1) - (A - 1) * cw + sA), b1: 2 * A * ((A - 1) - (A + 1) * cw), b2: A * ((A + 1) - (A - 1) * cw - sA),
        a0: (A + 1) + (A - 1) * cw + sA, a1: -2 * ((A - 1) + (A + 1) * cw), a2: (A + 1) + (A - 1) * cw - sA
      };
    }
    return {
      b0: A * ((A + 1) + (A - 1) * cw + sA), b1: -2 * A * ((A - 1) + (A + 1) * cw), b2: A * ((A + 1) + (A - 1) * cw - sA),
      a0: (A + 1) - (A - 1) * cw + sA, a1: 2 * ((A - 1) - (A + 1) * cw), a2: (A + 1) - (A - 1) * cw - sA
    };
  }
  function magDb(c, t) {
    var nr = c.b0 + c.b1 * t.c1 + c.b2 * t.c2, ni = -(c.b1 * t.s1 + c.b2 * t.s2);
    var dr = c.a0 + c.a1 * t.c1 + c.a2 * t.c2, di = -(c.a1 * t.s1 + c.a2 * t.s2);
    var num = Math.sqrt(nr * nr + ni * ni), den = Math.sqrt(dr * dr + di * di);
    return 20 * Math.log10((num + 1e-12) / (den + 1e-12));
  }
  // The curve the whole cascade really produces — dB of biquads in series add.
  function cascadeDb(filters, gains, trig) {
    var out = new Float64Array(trig.length), i, g;
    for (i = 0; i < filters.length; i++) {
      if (Math.abs(gains[i]) < 0.3) continue;                     // chain skips these too
      var c = coeffs(filters[i].type, filters[i].freq, gains[i], filters[i].q, filters[i]._sr);
      for (g = 0; g < trig.length; g++) out[g] += magDb(c, trig[g]);
    }
    return out;
  }

  // Gauss-Jordan with partial pivoting; n is ~12 here.
  function solve(A, b) {
    var n = b.length, M = [], r, c, k;
    for (r = 0; r < n; r++) M.push(A[r].slice().concat([b[r]]));
    for (c = 0; c < n; c++) {
      var p = c;
      for (r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
      var tmp = M[c]; M[c] = M[p]; M[p] = tmp;
      var pv = M[c][c];
      if (!isFinite(pv) || Math.abs(pv) < 1e-12) return null;
      for (r = 0; r < n; r++) {
        if (r === c) continue;
        var f = M[r][c] / pv;
        for (k = c; k <= n; k++) M[r][k] -= f * M[c][k];
      }
    }
    var x = [];
    for (r = 0; r < n; r++) x.push(M[r][n] / M[r][r]);
    for (r = 0; r < n; r++) if (!isFinite(x[r])) return null;
    return x;
  }

  // Solve for the gains whose CASCADE lands on `want`, then bound the result.
  function fitCurve(want, filters, trig, capDb, fitN) {
    var n = filters.length, i, j, g, s;
    var N = fitN || trig.length;
    var shapes = [];
    for (i = 0; i < n; i++) {                                     // response at 1 dB
      var c = coeffs(filters[i].type, filters[i].freq, 1, filters[i].q, filters[i]._sr);
      var row = new Float64Array(trig.length);
      for (g = 0; g < trig.length; g++) row[g] = magDb(c, trig[g]);
      shapes.push(row);
    }
    var A = [], b = [];
    for (i = 0; i < n; i++) {
      var arow = [];
      for (j = 0; j < n; j++) {
        s = 0;
        for (g = 0; g < N; g++) s += shapes[i][g] * shapes[j][g];
        arow.push(s + (i === j ? RIDGE : 0));
      }
      A.push(arow);
      s = 0;
      for (g = 0; g < N; g++) s += shapes[i][g] * want[g];
      b.push(s);
    }
    var gains = solve(A, b);
    if (!gains) return null;
    for (i = 0; i < n; i++) gains[i] = clamp(gains[i], GAIN_CAP_DB);

    // Two corrections against the TRUE cascade: a peaking filter's dB response
    // is not exactly linear in its gain, and neighbours overlap.
    for (var pass = 0; pass < 2; pass++) {
      var got = cascadeDb(filters, gains, trig);
      var b2 = [];
      for (i = 0; i < n; i++) {
        s = 0;
        for (g = 0; g < N; g++) s += shapes[i][g] * (want[g] - got[g]);
        b2.push(s);
      }
      var d = solve(A, b2);
      if (!d) break;
      for (i = 0; i < n; i++) gains[i] = clamp(gains[i] + d[i], GAIN_CAP_DB);
    }

    // Enforce the ceiling on what the user actually hears, not on what we asked
    // for. Scaling the whole set keeps the shape and only softens the amount.
    var curve = cascadeDb(filters, gains, trig);
    var mean = 0;
    for (g = 0; g < N; g++) mean += curve[g];
    mean /= N;
    var peak = 0;
    for (g = 0; g < N; g++) peak = Math.max(peak, Math.abs(curve[g] - mean));
    if (peak > capDb) {
      var kf = capDb / peak;
      for (i = 0; i < n; i++) gains[i] *= kf;
    }
    return gains;
  }

  function clamp(v, lim) {
    if (!isFinite(v)) return 0;
    return v > lim ? lim : (v < -lim ? -lim : v);
  }

  function bandsFrom(filters, gains) {
    var out = [];
    for (var i = 0; i < filters.length; i++) {
      out.push({ type: filters[i].type, freq: filters[i].freq, q: filters[i].q, gainDb: gains[i] });
    }
    return out;
  }

  function meanOf(a) {
    var s = 0;
    for (var i = 0; i < a.length; i++) s += a[i];
    return s / a.length || 1e-9;
  }

  /* ── public entry points ──────────────────────────────────────────────────*/

  function prepare(sr) {
    var filters = filterSet(sr), i;
    for (i = 0; i < filters.length; i++) filters[i]._sr = sr;
    var grid = makeGrid();
    // The grid is fixed in Hz, so on a low-rate file its top points sit above
    // Nyquist — fit only the part that exists.
    var lim = 0.47 * sr, fitN = 0;
    for (i = 0; i < grid.length; i++) { if (grid[i] <= lim) fitN = i + 1; }
    return { filters: filters, grid: grid, trig: gridTrig(grid, sr), fitN: fitN || grid.length };
  }

  /* ── Reference → profile ──────────────────────────────────────────────────
     Everything the matcher ever asks of a reference is its average tonal shape
     and its side/mid ratio: a few hundred numbers. Reducing it at load time and
     dropping the audio is not just tidiness — a 5-minute stereo reference is
     ~105 MB of decoded float, held for the whole session next to the track being
     mastered, on machines that are already the ones running out of memory. It
     also makes several references affordable, and makes them comparable across
     sample rates (the grid is fixed in Hz). */
  function profileOf(buffer, label) {
    try {
      if (!buffer) return null;
      var sr = buffer.sampleRate, m = mid(buffer);
      var mRms = rmsOf(m);
      if (mRms < 1e-5) return null;                            // nothing to copy
      var N = fftSizeFor(m.length), acc = powerSpectrum(m, N);
      var grid = makeGrid(), levels = new Float64Array(grid.length), i;
      for (i = 0; i < grid.length; i++) levels[i] = levelAt(acc, N, sr, grid[i], INTEG_OCT);
      var stereo = buffer.numberOfChannels >= 2;
      // The side channel gets the same treatment, stored RELATIVE to the mid
      // level. That ratio per frequency IS the stereo image: how wide the
      // record is at 200 Hz versus at 8 kHz. One number (side/mid RMS) can only
      // say how wide it is on average.
      var sideLevels = null, s;
      if (stereo) {
        s = side(buffer);
        if (rmsOf(s) > 1e-6) {
          var sN = fftSizeFor(s.length), sAcc = powerSpectrum(s, sN);
          sideLevels = new Float64Array(grid.length);
          for (i = 0; i < grid.length; i++) sideLevels[i] = levelAt(sAcc, sN, sr, grid[i], INTEG_OCT) / mRms;
        }
      }
      // How loud the reference actually is. Measured here because the audio is
      // about to be released — and because "master mine as loud as this one" is
      // a question only the reference can answer. Optional: if the metering
      // module is not on the page the profile simply carries no loudness.
      var lufs = null;
      try {
        if (window.LoudnessR128 && window.LoudnessR128.measureIntegratedLUFS) {
          var lv = window.LoudnessR128.measureIntegratedLUFS(buffer);
          if (isFinite(lv)) lufs = lv;
        }
      } catch (e) { lufs = null; }

      return {
        kind: 'ref-profile', label: label || '', levels: levels, sideLevels: sideLevels,
        stereo: stereo, sideMid: stereo ? (sideRms(buffer) / midRms(buffer)) : null,
        lufs: lufs, sampleRate: sr, duration: buffer.duration || 0
      };
    } catch (e) { return null; }
  }

  // Several references average into one — the same thing LANDR's three-reference
  // mode does. Averaging in the LOG domain (each normalised to its own mean
  // first) is what keeps a loud reference from dominating a quiet one.
  function averageProfiles(list) {
    var use = [], i, j;
    for (i = 0; i < list.length; i++) if (list[i] && list[i].levels) use.push(list[i]);
    if (!use.length) return null;
    if (use.length === 1) return use[0];
    var n = use[0].levels.length, out = new Float64Array(n), wSum = 0, wCount = 0;
    var sideAcc = new Float64Array(n), sideCount = 0;
    for (i = 0; i < use.length; i++) {
      var mean = 0;
      for (j = 0; j < n; j++) mean += use[i].levels[j];
      mean = (mean / n) || 1e-9;
      for (j = 0; j < n; j++) out[j] += 20 * Math.log10(use[i].levels[j] / mean + 1e-9);
      if (use[i].sideMid != null) { wSum += use[i].sideMid; wCount++; }
      // Side levels are already relative to their own mid, so they average in
      // the log domain directly — no per-profile normalisation needed.
      if (use[i].sideLevels) {
        for (j = 0; j < n; j++) sideAcc[j] += 20 * Math.log10(use[i].sideLevels[j] + 1e-9);
        sideCount++;
      }
    }
    for (j = 0; j < n; j++) out[j] = Math.pow(10, (out[j] / use.length) / 20);
    var sideOut = null;
    if (sideCount) {
      sideOut = new Float64Array(n);
      for (j = 0; j < n; j++) sideOut[j] = Math.pow(10, (sideAcc[j] / sideCount) / 20);
    }
    // Loudness averages in LUFS (already a log scale), over the references that
    // carry one.
    var lSum = 0, lCount = 0;
    for (i = 0; i < use.length; i++) if (typeof use[i].lufs === 'number') { lSum += use[i].lufs; lCount++; }
    return {
      kind: 'ref-profile', label: use.map(function (p) { return p.label; }).join(' + '),
      levels: out, sideLevels: sideOut, stereo: wCount > 0, sideMid: wCount ? wSum / wCount : null,
      lufs: lCount ? lSum / lCount : null,
      sampleRate: use[0].sampleRate, duration: 0, count: use.length
    };
  }

  /* How far apart two profiles are, in the matcher's own terms: the RMS (dB)
     of the shape difference the matcher would try to correct, with the same
     per-band clamp it applies. Small number = this reference asks for little
     EQ, i.e. it already sounds like the track. Pure profile math — the page
     computes the track's own profile once and ranks every reference against
     it without touching audio again. Only grid points BOTH files can carry
     take part (a 22.05 kHz reference has nothing to say above ~10 kHz). */
  function profileDistance(a, b) {
    if (!a || !b || !a.levels || !b.levels) return null;
    var grid = makeGrid(), i;
    var lim = 0.47 * Math.min(a.sampleRate || 48000, b.sampleRate || 48000), n = 0;
    for (i = 0; i < grid.length; i++) if (grid[i] <= lim) n = i + 1;
    if (!n) n = grid.length;
    var aM = meanOf(Array.prototype.slice.call(a.levels, 0, n));
    var bM = meanOf(Array.prototype.slice.call(b.levels, 0, n));
    if (!aM || !bM) return null;
    var s = 0;
    for (i = 0; i < n; i++) {
      var d = 20 * Math.log10(b.levels[i] / bM + 1e-6) - 20 * Math.log10(a.levels[i] / aM + 1e-6);
      d = clamp(d, GAIN_CAP_DB);
      s += d * d;
    }
    return Math.sqrt(s / n);
  }

  // The one implementation. A reference buffer is reduced to a profile first,
  // so "matched against a file" and "matched against a stored profile" cannot
  // drift apart — there is only one path.
  /* opts (optional, the music page passes none — behaviour unchanged):
       opts.shapeWant(freqs, want, tShape, rShape) — rewrite the desired curve
         in place before it is fitted. /acx-mastering uses it to keep its hands
         off what belongs to the VOICE rather than to the recording: matched to
         another narrator, the low band (pitch and chest) is left alone and the
         rest is followed only part-way. Measured 2026-10-22 on 13 LibriVox
         narrators: a different narrator asks this matcher for 3.85 dB RMS of
         correction and hits the 8 dB ceiling in 52 of 90 pairs — that is the
         other person's voice, not their microphone.
       opts.capDb — ceiling on the produced curve (default CURVE_CAP_DB). */
  function analyze(targetBuffer, reference, opts) {
    try {
      if (!targetBuffer || !reference) return { bands: [], widthFactor: 1, ok: false };
      var profile = null;
      if (reference.kind === 'ref-profile') profile = reference;
      else if (Object.prototype.toString.call(reference) === '[object Array]') profile = averageProfiles(reference);
      else profile = profileOf(reference);
      if (!profile) return { bands: [], widthFactor: 1, ok: false };
      return matchProfile(targetBuffer, profile, opts);
    } catch (e) {
      return { bands: [], widthFactor: 1, ok: false };
    }
  }

  function matchProfile(targetBuffer, profile, opts) {
    try {
      var sr = targetBuffer.sampleRate;
      var ctx = prepare(sr);
      if (!ctx.filters.length) return { bands: [], widthFactor: 1, ok: false };

      var tMid = mid(targetBuffer);
      // A track with no signal has nothing to correct, and its band ratios would
      // all collapse to the clamp.
      if (rmsOf(tMid) < 1e-5) return { bands: [], widthFactor: 1, ok: false };
      var tN = fftSizeFor(tMid.length), tAcc = powerSpectrum(tMid, tN);

      var tLv = [], i;
      for (i = 0; i < ctx.grid.length; i++) tLv.push(levelAt(tAcc, tN, sr, ctx.grid[i], INTEG_OCT));
      // Normalise each spectrum to its own mean: we match SHAPE, not level —
      // absolute loudness is set later by the platform target. Only the part of
      // the grid this track can actually carry takes part in the mean.
      var tM = meanOf(tLv.slice(0, ctx.fitN)), rM = meanOf(Array.prototype.slice.call(profile.levels, 0, ctx.fitN));
      var want = new Float64Array(ctx.grid.length);
      var tShape = new Float64Array(ctx.grid.length), rShape = new Float64Array(ctx.grid.length);
      for (i = 0; i < ctx.grid.length; i++) {
        tShape[i] = 20 * Math.log10(tLv[i] / tM + 1e-6);
        rShape[i] = 20 * Math.log10(profile.levels[i] / rM + 1e-6);
        want[i] = clamp(rShape[i] - tShape[i], GAIN_CAP_DB);
      }

      if (opts && typeof opts.shapeWant === 'function') opts.shapeWant(ctx.grid, want, tShape, rShape);
      var capOpt = (opts && opts.capDb > 0) ? Math.min(opts.capDb, CURVE_CAP_DB) : CURVE_CAP_DB;
      var gains = fitCurve(want, ctx.filters, ctx.trig, capOpt, ctx.fitN);
      if (!gains) return { bands: [], widthFactor: 1, ok: false };

      // Stereo width: match side/mid ratio. A MONO reference carries no width
      // information at all — reading its (absent) side channel as zero is how
      // this used to narrow the master to the 0.6 clamp floor, silently, on any
      // mono reference the user happened to pick. Only a stereo reference gets a
      // vote; "stereo file whose content happens to be centred" still does,
      // because that IS a measured width.
      var widthFactor = 1, tRatio = 0;
      if (profile.stereo && profile.sideMid != null) {
        tRatio = sideRms(targetBuffer) / midRms(targetBuffer);
        widthFactor = (tRatio > 1e-4) ? (profile.sideMid / tRatio) : 1;
        if (widthFactor > 1.6) widthFactor = 1.6; else if (widthFactor < 0.6) widthFactor = 0.6;
        if (!isFinite(widthFactor)) widthFactor = 1;
      }

      /* ── Width, per frequency ────────────────────────────────────────────
         One width number says how wide the record is on average. It cannot say
         that the reference is narrow at 200 Hz and wide at 8 kHz — which is
         most of what a stereo image IS. Matched here as a second curve, fitted
         to the SIDE channel relative to the mid, so it carries both the shape
         and the amount. Measured on a ground-truth bench (a known EQ applied to
         the side channel only): the side image lands 1.2 dB off with the single
         factor and 0.1 dB off with this — and the mid channel, i.e. the mono
         sum, is untouched by construction (0.000 dB, verified). */
      var sideBands = null, sideSpectra = null;
      if (profile.sideLevels && targetBuffer.numberOfChannels >= 2 && tRatio > SIDE_MIN_RATIO) {
        var tSide = side(targetBuffer);
        var sN = fftSizeFor(tSide.length), sAcc = powerSpectrum(tSide, sN);
        var tMidRms = midRms(targetBuffer) || 1e-9;
        var sWant = new Float64Array(ctx.grid.length);
        var tSideShape = new Float64Array(ctx.grid.length), rSideShape = new Float64Array(ctx.grid.length);
        for (i = 0; i < ctx.grid.length; i++) {
          var tRel = levelAt(sAcc, sN, sr, ctx.grid[i], INTEG_OCT) / tMidRms;
          var rRel = profile.sideLevels[i];
          tSideShape[i] = 20 * Math.log10(tRel + 1e-9);
          rSideShape[i] = 20 * Math.log10(rRel + 1e-9);
          // Below the mono-safe corner the width is left exactly as it is: bass
          // that is not mono does not survive a club system or a record cutter,
          // and no reference is worth that.
          sWant[i] = ctx.grid[i] < SIDE_MONO_HZ ? 0 : clamp(rSideShape[i] - tSideShape[i], GAIN_CAP_DB);
        }
        var sGains = fitCurve(sWant, ctx.filters, ctx.trig, SIDE_CAP_DB, ctx.fitN);
        if (sGains) {
          sideBands = bandsFrom(ctx.filters, sGains);
          sideSpectra = { target: tSideShape, reference: rSideShape };
        }
      }

      return {
        bands: bandsFrom(ctx.filters, gains), widthFactor: widthFactor, ok: true,
        sideBands: sideBands,
        refCount: profile.count || 1, refLabel: profile.label || '',
        spectra: { freqs: ctx.grid, target: tShape, reference: rShape, side: sideSpectra }
      };
    } catch (e) {
      return { bands: [], widthFactor: 1, ok: false };
    }
  }

  // How much of the match to apply, 0..1 — the control every paid matcher has
  // and this one did not. Scaling the fitted gains keeps the SHAPE of the
  // correction and only softens its amount; the width nudge relaxes toward 1
  // in step, so "50%" means half of both, not half of one of them.
  function withAmount(spec, amount) {
    if (!spec || !spec.bands) return spec;
    var a = (typeof amount === 'number' && isFinite(amount)) ? Math.max(0, Math.min(1, amount)) : 1;
    if (a === 1) return spec;
    function scale(list) {
      if (!list) return null;
      var o = [], i;
      for (i = 0; i < list.length; i++) o.push({ type: list[i].type, freq: list[i].freq, q: list[i].q, gainDb: list[i].gainDb * a });
      return o;
    }
    return {
      bands: scale(spec.bands), sideBands: scale(spec.sideBands),
      widthFactor: 1 + ((spec.widthFactor || 1) - 1) * a,
      ok: spec.ok, profile: spec.profile, spectra: spec.spectra, amount: a
    };
  }

  // Turn a profile into the same {bands, widthFactor} contract analyze() returns,
  // by comparing the profile's target shape with THIS track's own shape.
  // The profile is defined on the 9 CENTERS, so its correction is derived there
  // (unchanged, ±6 dB) and then interpolated onto the fitting grid — the genre
  // presets keep exactly the intent they were measured with, and only gain the
  // accurate realisation.
  /* Жанрово-НЕЙТРАЛЬНАЯ цель для авторежима: это сам BASELINE — измеренная
     средняя тональная форма настоящих мастеров, из которой и собраны все
     жанровые профили (жанр = BASELINE + маленькая дельта). Поэтому «выровнять
     к типичному мастеру, не спрашивая жанр» — не новая эвристика, а тот же
     проверенный путь с нулевой дельтой. Ширину авторежим НЕ трогает (1.0):
     ширина зависит от аранжировки, а не от «нормы», и раздвигать её вслепую —
     как раз тот случай, когда автомат портит материал. */
  var NEUTRAL_PROFILE = { db: BASELINE.slice(), width: 1 };

  function analyzeProfile(targetBuffer, key) {
    try {
      var prof = (key === 'auto') ? NEUTRAL_PROFILE : GENRE_PROFILES[key];
      var capDb = (key === 'auto') ? AUTO_CAP_DB : PROFILE_CAP_DB;
      if (!targetBuffer || !prof) return { bands: [], widthFactor: 1, ok: false };
      var sr = targetBuffer.sampleRate;
      var ctx = prepare(sr);
      if (!ctx.filters.length) return { bands: [], widthFactor: 1, ok: false };

      var tMid = mid(targetBuffer);
      var tN = fftSizeFor(tMid.length), tAcc = powerSpectrum(tMid, tN);
      var tB = [], i;
      for (i = 0; i < CENTERS.length; i++) tB.push(levelAt(tAcc, tN, sr, CENTERS[i], BAND_INTEG));
      var tM = meanOf(tB);
      var pLin = [];
      for (i = 0; i < prof.db.length; i++) pLin.push(Math.pow(10, prof.db[i] / 20));
      var pM = meanOf(pLin);
      var perBand = [];
      for (i = 0; i < CENTERS.length; i++) {
        perBand.push(clamp(20 * Math.log10((pLin[i] / pM + 1e-6) / (tB[i] / tM + 1e-6)), capDb));
      }

      // linear interpolation in log-frequency, held flat past the end centres
      var want = new Float64Array(ctx.grid.length);
      for (i = 0; i < ctx.grid.length; i++) {
        var f = ctx.grid[i];
        if (f <= CENTERS[0]) { want[i] = perBand[0]; continue; }
        if (f >= CENTERS[CENTERS.length - 1]) { want[i] = perBand[CENTERS.length - 1]; continue; }
        for (var c = 1; c < CENTERS.length; c++) {
          if (f <= CENTERS[c]) {
            var t = Math.log(f / CENTERS[c - 1]) / Math.log(CENTERS[c] / CENTERS[c - 1]);
            want[i] = perBand[c - 1] + t * (perBand[c] - perBand[c - 1]);
            break;
          }
        }
      }

      var gains = fitCurve(want, ctx.filters, ctx.trig, capDb, ctx.fitN);
      if (!gains) return { bands: [], widthFactor: 1, ok: false };

      // Same two curves the uploaded-reference path draws: the track's own tonal
      // shape on the fitting grid, and the shape the genre asks for.
      var tGrid = [], tShape = new Float64Array(ctx.grid.length), rShape = new Float64Array(ctx.grid.length);
      for (i = 0; i < ctx.grid.length; i++) tGrid.push(levelAt(tAcc, tN, sr, ctx.grid[i], INTEG_OCT));
      var tGm = meanOf(tGrid);
      for (i = 0; i < ctx.grid.length; i++) {
        tShape[i] = 20 * Math.log10(tGrid[i] / tGm + 1e-6);
        rShape[i] = tShape[i] + want[i];
      }

      var w = prof.width;                                   // relative nudge, already sane
      if (!isFinite(w) || w <= 0) w = 1;
      if (w > 1.6) w = 1.6; else if (w < 0.6) w = 0.6;
      return {
        bands: bandsFrom(ctx.filters, gains), widthFactor: w, ok: true, profile: key,
        // Отклонение трека от цели по 9 полосам, в дБ (положительное = нам надо
        // ДОБАВИТЬ). Авторежим показывает это пользователю словами — «что мы
        // услышали», — а не просто молча правит.
        perBandDb: perBand,
        spectra: { freqs: ctx.grid, target: tShape, reference: rShape }
      };
    } catch (e) {
      return { bands: [], widthFactor: 1, ok: false };
    }
  }

  // What the returned bands will really do, on a log grid — for tests, and for
  // any UI that wants to draw the correction rather than assert it.
  function responseCurve(bands, sr, freqs) {
    var grid = freqs || makeGrid(sr);
    var trig = gridTrig(grid, sr), out = new Float64Array(grid.length), i, g;
    for (i = 0; i < bands.length; i++) {
      if (Math.abs(bands[i].gainDb) < 0.3) continue;
      var c = coeffs(bands[i].type || 'peaking', bands[i].freq, bands[i].gainDb, bands[i].q || 1.0, sr);
      for (g = 0; g < grid.length; g++) out[g] += magDb(c, trig[g]);
    }
    return { freqs: grid, db: out };
  }

  window.MasteringReference = {
    analyze: analyze,
    analyzeProfile: analyzeProfile,
    // Авторежим: та же машинерия, цель — нейтральный BASELINE.
    analyzeAuto: function (buffer) { return analyzeProfile(buffer, 'auto'); },
    BASELINE: BASELINE,
    profileOf: profileOf,
    averageProfiles: averageProfiles,
    profileDistance: profileDistance,
    withAmount: withAmount,
    bandLevels: bandLevels,
    responseCurve: responseCurve,
    CENTERS: CENTERS,
    CURVE_CAP_DB: CURVE_CAP_DB,
    GENRE_PROFILES: GENRE_PROFILES,
    genres: function () { var k = []; for (var g in GENRE_PROFILES) if (GENRE_PROFILES.hasOwnProperty(g)) k.push(g); return k; }
  };
})();
